import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

const ChatbotContext = createContext();

export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error('useChatbot must be used within ChatbotProvider');
  }
  return context;
};

/**
 * Chatbot modes:
 * - 'search': Property search assistant (default for non-authenticated)
 * - 'assistant': AI assistant with data access (for authenticated users)
 */
export const ChatbotProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('search'); // 'search' | 'assistant'
  const [isInlineMode, setIsInlineMode] = useState(false); // For hero section inline chat
  
  // Search mode state
  const [searchSessionId, setSearchSessionId] = useState(null);
  const [searchMessages, setSearchMessages] = useState([]);
  const [searchProperties, setSearchProperties] = useState([]);
  
  // Assistant mode state
  const [assistantSessionId, setAssistantSessionId] = useState(null);
  const [assistantMessages, setAssistantMessages] = useState([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Determine the default mode based on user role
   */
  const getDefaultMode = useCallback(() => {
    if (!isAuthenticated) return 'search';
    
    const role = user?.role?.toUpperCase();
    // Admin and Professional always get assistant mode
    if (role === 'ADMIN' || role === 'PROFESSIONAL') {
      return 'assistant';
    }
    // Clients start with search but can toggle
    return 'search';
  }, [isAuthenticated, user]);

  /**
   * Check if user can toggle between modes (only clients can)
   */
  const canToggleMode = useCallback(() => {
    if (!isAuthenticated) return false;
    const role = user?.role?.toUpperCase();
    return role === 'CLIENT';
  }, [isAuthenticated, user]);

  /**
   * Get the available modes for current user
   */
  const getAvailableModes = useCallback(() => {
    if (!isAuthenticated) return ['search'];
    
    const role = user?.role?.toUpperCase();
    if (role === 'ADMIN' || role === 'PROFESSIONAL') {
      return ['assistant'];
    }
    // Clients can use both
    return ['search', 'assistant'];
  }, [isAuthenticated, user]);

  /**
   * Toggle between search and assistant mode (for clients)
   */
  const toggleMode = useCallback(() => {
    if (!canToggleMode()) return;
    
    setMode(prev => prev === 'search' ? 'assistant' : 'search');
  }, [canToggleMode]);

  /**
   * Open chatbot with specific mode
   * Respects inline mode - opens full modal even when inline exists
   */
  const openChatbot = useCallback((specificMode = null) => {
    const targetMode = specificMode || getDefaultMode();
    setMode(targetMode);
    setIsOpen(true);
  }, [getDefaultMode]);

  /**
   * Close chatbot
   */
  const closeChatbot = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Clear current session
   */
  const clearSession = useCallback(() => {
    if (mode === 'search') {
      setSearchSessionId(null);
      setSearchMessages([]);
      setSearchProperties([]);
    } else {
      setAssistantSessionId(null);
      setAssistantMessages([]);
    }
  }, [mode]);

  /**
   * Add message to current mode's messages
   */
  const addMessage = useCallback((message) => {
    if (mode === 'search') {
      setSearchMessages(prev => [...prev, message]);
    } else {
      setAssistantMessages(prev => [...prev, message]);
    }
  }, [mode]);

  /**
   * Get current session ID
   */
  const getCurrentSessionId = useCallback(() => {
    return mode === 'search' ? searchSessionId : assistantSessionId;
  }, [mode, searchSessionId, assistantSessionId]);

  /**
   * Set current session ID
   */
  const setCurrentSessionId = useCallback((sessionId) => {
    if (mode === 'search') {
      setSearchSessionId(sessionId);
    } else {
      setAssistantSessionId(sessionId);
    }
  }, [mode]);

  /**
   * Get current messages
   */
  const getCurrentMessages = useCallback(() => {
    return mode === 'search' ? searchMessages : assistantMessages;
  }, [mode, searchMessages, assistantMessages]);

  /**
   * Get role-specific configuration
   */
  const getRoleConfig = useCallback(() => {
    const role = user?.role?.toUpperCase() || 'GUEST';
    
    const configs = {
      GUEST: {
        title: 'Cynthia',
        subtitle: 'Recherche conversationnelle',
        headerColor: 'bg-[#FF9F68]',
        headerGradient: null,
        icon: 'search',
        welcomeMessage: 'Bonjour ! Je suis là pour vous aider à trouver votre bien immobilier idéal. Décrivez-moi ce que vous recherchez (ville, type de bien, budget, nombre de pièces...).',
        suggestions: [
          'Appartement 3 pièces Genève',
          'Villa à vendre Lausanne',
          'Studio meublé Zürich',
          'Maison avec jardin Basel'
        ]
      },
      CLIENT: {
        title: mode === 'search' ? 'Recherche Immobilière' : 'Cynthia',
        subtitle: mode === 'search' ? 'Trouvez votre bien idéal' : 'Votre assistante IA',
        headerColor: 'bg-[#FF9F68]',
        headerGradient: mode === 'assistant' ? 'bg-gradient-to-r from-blue-600 to-blue-800' : null,
        icon: mode === 'search' ? 'search' : 'assistant',
        welcomeMessage: mode === 'search' 
          ? 'Bonjour ! Décrivez le bien que vous recherchez.'
          : `Bonjour ${user?.firstName || ''} ! Je suis Cynthia, votre assistante IA. Je suis là pour vous accompagner dans votre recherche immobilière. Je peux vous aider à suivre vos demandes, consulter vos visites planifiées, voir vos biens favoris, et vous guider dans votre projet. Comment puis-je vous aider ?`,
        suggestions: mode === 'search' 
          ? ['Appartement Genève', 'Villa Lausanne', 'Studio Zürich']
          : [
              'Où en sont mes demandes ?',
              'Quand est ma prochaine visite ?',
              'Montre-moi mes favoris',
              'Recherche des appartements à Genève'
            ]
      },
      PROFESSIONAL: {
        title: 'Cynthia',
        subtitle: 'Votre assistante IA',
        headerColor: null,
        headerGradient: 'bg-gradient-to-r from-emerald-600 to-teal-700',
        icon: 'assistant',
        welcomeMessage: `Bonjour ${user?.firstName || ''} ! Je suis Cynthia, votre assistante IA. Je suis là pour vous accompagner dans votre activité professionnelle. Je peux vous informer sur vos leads, vos visites planifiées, les statistiques de vos biens, et vous aider à optimiser votre activité quotidienne. Que souhaitez-vous savoir ?`,
        suggestions: [
          'Combien de leads en attente ?',
          'Quelles sont mes visites cette semaine ?',
          'Statistiques de mes biens',
          'Détails du dernier lead'
        ]
      },
      ADMIN: {
        title: 'Cynthia',
        subtitle: 'Votre assistante IA',
        headerColor: null,
        headerGradient: 'bg-gradient-to-r from-purple-600 to-indigo-700',
        icon: 'assistant',
        welcomeMessage: `Bonjour ${user?.firstName || 'Admin'} ! Je suis Cynthia, votre assistante IA. Je suis là pour vous accompagner dans la gestion de votre plateforme. Je peux vous donner un aperçu du tableau de bord, vous informer sur les demandes en cours, les performances de l'équipe, et vous aider à prendre les meilleures décisions. Comment puis-je vous aider aujourd'hui ?`,
        suggestions: [
          'Résumé du tableau de bord',
          'Demandes en attente',
          'Performance de l\'équipe',
          'Liste des professionnels'
        ]
      }
    };

    return configs[role] || configs.GUEST;
  }, [user, mode]);

  const value = {
    // State
    isOpen,
    mode,
    isLoading,
    isInlineMode,
    searchMessages,
    searchProperties,
    assistantMessages,
    
    // Setters
    setIsOpen,
    setMode,
    setIsLoading,
    setIsInlineMode,
    setSearchMessages,
    setSearchProperties,
    setAssistantMessages,
    
    // Actions
    openChatbot,
    closeChatbot,
    toggleMode,
    clearSession,
    addMessage,
    
    // Session management
    getCurrentSessionId,
    setCurrentSessionId,
    getCurrentMessages,
    
    // Computed
    canToggleMode: canToggleMode(),
    availableModes: getAvailableModes(),
    roleConfig: getRoleConfig(),
    
    // User context
    isAuthenticated,
    userRole: user?.role?.toUpperCase() || 'GUEST',
  };

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  );
};
